import {
  Button,
  Modal,
  Select,
  Card,
  Tooltip,
  Divider,
  Typography,
  Alert,
} from "antd";
import React, { useEffect, useState } from "react";
import Table, { TableColumn } from "../../../../components/Table";
import Maximize from "../../../../components/Maximize";
import HeatMap from "../../../../components/HeatMap";
import { InfoCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import {
  FinalizeDependentVariablesProps,
  LiftData,
} from "../../../../models/interfaces";
import LineChart from "../../../../components/LineChart";

const { Title, Paragraph, Text } = Typography;

const multiColinearityColumn: TableColumn[] = [
  { key: "variable", label: "Variable" },
  { key: "value", label: "Value" },
];

const FinalizeDependentVariables: React.FC<FinalizeDependentVariablesProps> = ({
  csvData,
  columnMappings,
  metaFields,
  variableSummaryStatistic,
  multiColinearityRecords,
  heatMapData,
  allVariiables,
  dependentVariables,
  seasonLabel,
  seasonDataSet,
  setDependentVariables,
  submitFeatureForm,
  isEditMode = false,
  isViewOnly = false,
  loading = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const allOptions = [
    ...allVariiables.spendVariable.items,
    ...allVariiables.impressionVariable.items,
    ...allVariiables.nonMediaVariable.items,
  ]
    .filter((ele: any) => ele.label !== "Date")
    .map((item) => ({
      value: item.label,
      label: item.label,
    }));

  return (
    <div className="p-4">
      <Alert
        message="Variable Selection"
        description="Select the dependent variables that are closely related to your KPIs. Use the correlation matrix and multi-collinearity check to make informed decisions."
        type="info"
        showIcon
        className="mb-6"
      />

      <div className="grid grid-cols-1 gap-8">
        {/* Multi-collinearity Section */}
        <Card
          title={
            <div className="flex items-center">
              <span className="text-lg font-semibold text-gray-800">
                Multi-Collinearity Check
              </span>
              <Tooltip title="Multi-collinearity occurs when independent variables are highly correlated with each other, which can affect model accuracy">
                <InfoCircleOutlined className="ml-2 text-blue-500" />
              </Tooltip>
            </div>
          }
          className="shadow-md hover:shadow-lg transition-shadow duration-300"
          bordered={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
            <div>
              <Paragraph className="text-gray-600 mb-4">
                Review the VIF (Variance Inflation Factor) values for each
                variable. Values greater than 10 indicate high
                multi-collinearity.
              </Paragraph>
              <Table
                columns={multiColinearityColumn}
                records={multiColinearityRecords?.filter(
                  (item: any) =>
                    item?.variable !== "const" && item?.variable !== "Sales"
                )}
              />
            </div>
          </div>
        </Card>

        {/* Correlation Matrix Section */}
        <Card
          title={
            <div className="flex items-center">
              <span className="text-lg font-semibold text-gray-800">
                Correlation Matrix
              </span>
              <Tooltip title="The correlation matrix shows the relationship strength between variables">
                <InfoCircleOutlined className="ml-2 text-blue-500" />
              </Tooltip>
            </div>
          }
          className="shadow-md hover:shadow-lg transition-shadow duration-300"
          bordered={false}
        >
          <div className="width-full">
            {heatMapData ? (
              <Maximize title={"Correlation Matrix"}>
                <HeatMap data={heatMapData} />
              </Maximize>
            ) : (
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <Text type="secondary">No correlation data available</Text>
              </div>
            )}
          </div>
        </Card>

        {/* Seasonality Section */}
        <Card
          title={
            <div className="flex items-center">
              <span className="text-lg font-semibold text-gray-800">
                Seasonality Analysis
              </span>
              <Tooltip title="Seasonality shows recurring patterns in your data over time">
                <InfoCircleOutlined className="ml-2 text-blue-500" />
              </Tooltip>
            </div>
          }
          className="shadow-md hover:shadow-lg transition-shadow duration-300"
          bordered={false}
        >
          <div className="w-full">
            {seasonLabel && seasonLabel.length > 0 ? (
              <Maximize title="Seasonality">
                <LineChart
                  labels={seasonLabel}
                  dataSets={seasonDataSet}
                  yAxisTitle="Level Component For Sale"
                  xAxisTitle="Date"
                />
              </Maximize>
            ) : (
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <Text type="secondary">No seasonality data available</Text>
              </div>
            )}
          </div>
        </Card>

        {/* Variable Selection Section */}
        <Card
          title={
            <div className="flex items-center">
              <span className="text-lg font-semibold text-gray-800">
                Select Dependent Variables
              </span>
              <Tooltip title="Choose variables that you want to analyze in your model">
                <InfoCircleOutlined className="ml-2 text-blue-500" />
              </Tooltip>
            </div>
          }
          className="shadow-md hover:shadow-lg transition-shadow duration-300"
          bordered={false}
        >
          <Paragraph className="text-gray-600 mb-4">
            Based on the correlation matrix and multi-collinearity check, select
            the variables that are most relevant to your analysis. Choose
            variables that have strong correlation with your target but low
            correlation with each other.
          </Paragraph>

          <div className="mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Text strong className="text-gray-700">
                  Spend Variables
                </Text>
                <div className="bg-blue-50 p-2 rounded-md mt-1">
                  {allVariiables.spendVariable.items.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {allVariiables.spendVariable.items
                        .filter((item: any) => item.label !== "Date")
                        .map((item: any, index: number) => (
                          <li key={index} className="text-sm text-gray-600">
                            {item.label}
                            {dependentVariables.includes(item.label) && (
                              <CheckCircleOutlined className="ml-2 text-green-500" />
                            )}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <Text type="secondary" className="text-sm">
                      No spend variables available
                    </Text>
                  )}
                </div>
              </div>

              <div>
                <Text strong className="text-gray-700">
                  Impression Variables
                </Text>
                <div className="bg-green-50 p-2 rounded-md mt-1">
                  {allVariiables.impressionVariable.items.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {allVariiables.impressionVariable.items
                        .filter((item: any) => item.label !== "Date")
                        .map((item: any, index: number) => (
                          <li key={index} className="text-sm text-gray-600">
                            {item.label}
                            {dependentVariables.includes(item.label) && (
                              <CheckCircleOutlined className="ml-2 text-green-500" />
                            )}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <Text type="secondary" className="text-sm">
                      No impression variables available
                    </Text>
                  )}
                </div>
              </div>

              <div>
                <Text strong className="text-gray-700">
                  Non-Media Variables
                </Text>
                <div className="bg-purple-50 p-2 rounded-md mt-1">
                  {allVariiables.nonMediaVariable.items.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {allVariiables.nonMediaVariable.items
                        .filter((item: any) => item.label !== "Date")
                        .map((item: any, index: number) => (
                          <li key={index} className="text-sm text-gray-600">
                            {item.label}
                            {dependentVariables.includes(item.label) && (
                              <CheckCircleOutlined className="ml-2 text-green-500" />
                            )}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <Text type="secondary" className="text-sm">
                      No non-media variables available
                    </Text>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Divider className="my-4" />

          <div>
            <Text strong className="text-gray-700 text-lg">
              Make Your Selection
            </Text>
            <Select
              className="mt-3"
              mode="multiple"
              options={allOptions}
              value={dependentVariables}
              onChange={(value) => setDependentVariables(value)}
              placeholder="Select variables for your model"
              style={{ width: "100%" }}
              optionFilterProp="label"
              showSearch
              disabled={isViewOnly}
              tagRender={(props) => (
                <Tooltip title={`Selected: ${props.label}`}>
                  <span className="ant-select-selection-item">
                    <span className="ant-select-selection-item-content">
                      {props.label}
                    </span>
                    <span
                      className="ant-select-selection-item-remove"
                      onClick={props.onClose}
                    >
                      ×
                    </span>
                  </span>
                </Tooltip>
              )}
            />

            {dependentVariables.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 rounded-md">
                <Text strong className="text-green-700">
                  <CheckCircleOutlined className="mr-2" />
                  {dependentVariables.length} variables selected
                </Text>
              </div>
            )}
          </div>

          {/* <div className="mt-6 flex justify-end">
            <Button
              type="primary"
              size="large"
              loading={loading}
              onClick={() => submitFeatureForm({})}
            >
              {isEditMode ? "Update Model" : "Create Model"}
            </Button>
          </div> */}
        </Card>
      </div>

      <Modal
        width={"100%"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => setModalOpen(false)}
        title="Detailed Correlation Matrix"
      >
        {heatMapData && (
          <Maximize title={"Correlation Matrix"}>
            <HeatMap data={heatMapData} />
          </Maximize>
        )}
      </Modal>
    </div>
  );
};

export default FinalizeDependentVariables;
